import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Box, Text, HStack, Button, SimpleGrid, useToast, Heading, Spacer, Divider, Flex } from '@chakra-ui/react';
import LoadingState from '../components/ui/LoadingState';
import EmptyState from '../components/ui/EmptyState';
import Page from '../components/ui/Page';
import ComprehensiveFilterBar from '../components/filters/ComprehensiveFilterBar';
import ComprehensiveFilterChips from '../components/filters/ComprehensiveFilterChips';
import SearchInput from '../components/filters/SearchInput';
import { useNavigate } from 'react-router-dom';
import TestGroupCard from '../components/TestGroupCard';
import HelpTooltip from '../components/ui/HelpTooltip';
import client from '../utils/fetchClient';
import { useProjectContext } from '../context/ProjectContext';
import useComprehensiveFilterState from '../utils/useComprehensiveFilterState';
// import { InfoIcon } from '@chakra-ui/icons';



// Dynamic group mapping: group name -> array of keywords (unused - keeping for future reference)
// const GROUP_KEYWORDS = {
//   Assets: [
//     'asset management', 'asset categories', 'asset filters', 'assets', 'asset'
//   ],
//   Expenses: [
//     'expense_settings', 'expenses', 'expense'
//   ],
//   // Add more groups/modules here as needed
// };

// function getGroup(tc) {
//   if (!tc.title) return 'Unknown';
//   const parts = tc.title.split('›');
//   if (parts.length > 1) return parts[0].replace(/^@/, '').trim();
//   return 'Unknown';
// }

// function getMainGroup(tc) {
//   // Extract group name from suite field (e.g., "api/assets/assets.spec.js" -> "assets")
//   if (tc.suite) {
//     const parts = tc.suite.split('/');
//     if (parts.length >= 2) {
//       return parts[1]; // Return the second part (e.g., "assets" from "api/assets/assets.spec.js")
//     }
//   }
//   // Fallback to file field if suite is not available
//   if (tc.file) {
//     const parts = tc.file.split('/');
//     if (parts.length >= 2) {
//       return parts[1];
//     }
//   }
//   return getGroup(tc);
// }

// Helper to compute metrics for each group (unused - keeping for future reference)
// function computeGroupMetrics(cases) {
//   const total = cases.length;
//   const passed = cases.filter(tc => tc.status === 'passed').length;
//   const failed = cases.filter(tc => tc.status === 'failed').length;
//   const skipped = cases.filter(tc => tc.status === 'skipped').length;
//   const passRate = total > 0 ? Math.round((passed / total) * 100) : 0;
//   const lastRun = cases.reduce((max, tc) => tc.createdAt && (!max || tc.createdAt > max) ? tc.createdAt : max, null);
//   return { total, passed, failed, skipped, passRate, lastRun };
// }

function TestCaseGroupsPage() {
  const { projects, effectiveProjectParam } = useProjectContext();
  const navigate = useNavigate();
  const toast = useToast();
  
  // Use comprehensive filter state
  const { filters, updateFilter, buildApiParams } = useComprehensiveFilterState({
    period: 'weekly',
    dateRange: 'last7days'
  });
  
  const [testCases, setTestCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const CARDS_PER_PAGE = 6; // Show 6 cards per page

  // Fetch modules with proper pagination
  const fetchModules = useCallback(async () => {
    setLoading(true);
    const params = buildApiParams();
    
    // Remove date-related parameters
    delete params.dateRange;
    delete params.startDate;
    delete params.endDate;
    
    if (effectiveProjectParam) params.project = effectiveProjectParam;
    
    // Fetch all modules and handle pagination on frontend
    params.pageSize = 100; // Get all modules
    
    try {
      const res = await client.get('/api/modules', { query: params });
      const data = await res.json();
      
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data?.data)
        ? data.data
        : [];
      
      // API response logged for debugging
      
      setTestCases(list);
    } catch (error) {
      setTestCases([]);
    } finally {
      setLoading(false);
    }
  }, [effectiveProjectParam, buildApiParams]);

  // Initial fetch
  useEffect(() => {
    fetchModules();
  }, [fetchModules]);



  // Handle page change
  const handlePageChange = (newPage) => {
    const maxPages = Math.max(1, totalPages);
    if (newPage >= 1 && newPage <= maxPages) {
      setCurrentPage(newPage);
    }
  };

  // Process modules data to create test groups
  const groups = useMemo(() => {
    const safeModules = Array.isArray(testCases) ? testCases : []; 
    return safeModules.map(module => {
      // Extract a unique group identifier from the full path
      // For leaves: "api/leaves/leave_balance.spec.js" -> "leave_balance"
      // For payroll: "api/payroll/run_payroll.spec.js" -> "run_payroll"
      let groupName = 'Unknown';
      let displayName = 'Unknown';
      
      if (module.module) {
        const parts = module.module.split('/');
        if (parts.length >= 3) {
          // Use just the module name as the unique group identifier
          const moduleName = parts[2].replace('.spec.js', ''); // e.g., "leave_balance", "run_payroll"
          groupName = moduleName; // e.g., "leave_balance", "run_payroll"
          displayName = moduleName.replace(/_/g, ' '); // e.g., "leave balance", "run payroll"
        } else if (parts.length >= 2) {
          groupName = parts[1];
          displayName = parts[1];
        } else{
          groupName = parts[0];
          displayName = parts[0];
        }
      } 

      return {
        group: groupName, // Unique identifier for the module
        displayName: displayName, // Display name for the card
        runDate: new Date().toISOString(), // Use current date as modules don't have individual run dates
        cases: [], // Modules represent the group, not individual cases
        moduleData: module, // Store the full module data for metrics
        // Add computed metrics from module data
        totalTests: module.testCount || 0,
        passedTests: module.passedCount || 0,
        failedTests: module.failedCount || 0,
        skippedTests: module.skippedCount || 0,
        passRate: module.passRate || 0,
        failRate: module.failRate || 0,
        avgDuration: module.avgDuration || 0,
        totalDuration: module.totalDuration || 0
      };
    }).sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [testCases]);

  // Filtered and searched groups
  const filteredGroups = useMemo(() => {
    return groups.filter(({ group, displayName }) => {
      return group.toLowerCase().includes(searchTerm.toLowerCase()) || 
             displayName.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [groups, searchTerm]);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Calculate total pages based on filtered groups
  const totalPages = Math.ceil(filteredGroups.length / CARDS_PER_PAGE);

  // Ensure currentPage doesn't exceed totalPages
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  // Use the filtered groups for display with pagination
  const paginatedGroups = useMemo(() => { 
    const startIndex = (currentPage - 1) * CARDS_PER_PAGE;
    const endIndex = startIndex + CARDS_PER_PAGE;
    const pageGroups = filteredGroups.slice(startIndex, endIndex);

    
    return pageGroups;
  }, [filteredGroups, currentPage]);
 

  // Compute summary metrics for cards using modules data
  const summaryMetrics = useMemo(() => {
    const totalGroups = filteredGroups.length;
    const totalTests = testCases.reduce((sum, module) => sum + (module.testCount || 0), 0);
    const totalPassed = testCases.reduce((sum, module) => sum + (module.passedCount || 0), 0);
    const totalFailed = testCases.reduce((sum, module) => sum + (module.failedCount || 0), 0);
    const passRate = totalTests > 0 ? ((totalPassed / totalTests) * 100).toFixed(1) : '0.0';
    const failRate = totalTests > 0 ? ((totalFailed / totalTests) * 100).toFixed(1) : '0.0';
    const lastRun = new Date().toLocaleString(); // Use current date as modules don't have individual run dates
    
    return [
      { label: 'Total Groups', value: totalGroups, trend: 'neutral', tooltipText: 'Number of test groups.' },
      { label: 'Total Tests', value: totalTests, trend: 'neutral', tooltipText: 'Number of test cases.' },
      { label: 'Pass Rate', value: passRate + '%', trend: passRate >= 80 ? 'up' : 'down', tooltipText: 'Percentage of tests that passed.' },
      { label: 'Fail Rate', value: failRate + '%', trend: failRate <= 20 ? 'up' : 'down', tooltipText: 'Percentage of tests that failed.' },
      { label: 'Last Run', value: lastRun, trend: 'neutral', tooltipText: 'Most recent test run.' }
    ];
  }, [filteredGroups, testCases]);

  // Prepare filter options
  const filterOptions = {
    projects: projects,
    groups: groups.map(g => g.group)
  };

  if (loading) {
    return <LoadingState title="Loading test groups" subtitle="Fetching grouped test cases" minH="80vh" showSkeleton />;
  }
  
  const openTestGroupDrawer = (group) => {
    let splitGroup = group.split("\\");

    navigate('/test-cases/'+ splitGroup[splitGroup.length - 1]);
  };

  return (
    <Page>
      {/* Header with Filters */}
      <ComprehensiveFilterBar
        filters={filters}
        updateFilter={updateFilter}
        filterOptions={filterOptions}
        showProject={false}
        showDateRange={false}
        showPeriod={true}
        showGroup={false}
        showStatus={false}
        showEnvironment={false}
        showBranch={false}
        showUser={false}
        showCompareTo={false}
      >
        <Heading size="lg" fontWeight="bold" display="flex" alignItems="center">Test Groups <HelpTooltip label="Grouped view of test cases by module. Use filters to change time range and search by group." href="https://docs.example.com/qa/test-groups" /></Heading>
        <Spacer />
        <SearchInput
          placeholder="Search by group name..."
          value={searchTerm}
          onChange={setSearchTerm}
          onClear={() => setSearchTerm('')}
        />
      </ComprehensiveFilterBar>

      <ComprehensiveFilterChips 
        filters={filters} 
        updateFilter={updateFilter}
        showProject={false}
        showDateRange={false}
        showPeriod={true}
        showGroup={false}
        showStatus={false}
        showEnvironment={false}
        showBranch={false}
        showUser={false}
        showCompareTo={false}
      />
      
      {/* Search chip if search term exists */}
      {searchTerm && (
        <Flex wrap="wrap" gap={2} mb={4}>
          <Box
            display="inline-flex"
            alignItems="center"
            gap={2}
            px={3}
            py={1}
            bg="blue.100"
            color="blue.800"
            borderRadius="full"
            fontSize="sm"
            fontWeight="medium"
          >
            <Text>Search: {searchTerm}</Text>
            <Button
              size="xs"
              variant="ghost"
              color="blue.800"
              onClick={() => setSearchTerm('')}
              _hover={{ bg: 'blue.200' }}
            >
              ×
            </Button>
          </Box>
        </Flex>
      )}
      {/* Summary Cards Grid */}
      <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 5 }} spacing={6} mb={12}>
        {summaryMetrics.map((metric, idx) => (
          <Box
            key={metric.label}
            bg={metric.trend === 'up' ? 'green.50' : metric.trend === 'down' ? 'red.50' : 'blue.50'}
            borderRadius="xl"
            boxShadow="sm"
            borderWidth="2px"
            borderColor={metric.trend === 'up' ? 'green.200' : metric.trend === 'down' ? 'red.200' : 'blue.200'}
            p={3}
            minW={{ base: '120px', md: '140px' }}
            minH="64px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            fontWeight="bold"
            fontSize="lg"
            _hover={{ boxShadow: 'md', borderColor: metric.trend === 'up' ? 'green.400' : metric.trend === 'down' ? 'red.400' : 'blue.400' }}
            transition="box-shadow 0.2s, border-color 0.2s"
          >
            <Text fontSize="sm" color="gray.600" mb={1}>{metric.label}</Text>
            <Text fontSize="xl" color="gray.800">{metric.value}</Text>
          </Box>
        ))}
      </SimpleGrid>
      <Divider mb={8} borderColor="gray.200" borderWidth={2} />
      {/* Loading/Error States */}
      {loading && (
        <LoadingState title="Loading test groups" />
      )}
      {/* Main Content Area */}
      {filteredGroups.length === 0 ? (
        <EmptyState emoji="🗂️" title="No test groups found" subtitle="Try adjusting your filters or run some tests." />
      ) : (
         <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={6}>
          {paginatedGroups.map(({ group, displayName, runDate, moduleData, totalTests, passedTests, failedTests, skippedTests, passRate, failRate }) => {
            // Create metrics from module data
            const metrics = {
              total: totalTests,
              passed: passedTests,
              failed: failedTests,
              skipped: skippedTests,
              passRate: passRate,
              lastRun: runDate
            };
            
            // Handler for Run All
            const handleRunAll = async () => {
              try {
                const res = await client.post(`/api/test-groups/${encodeURIComponent(group)}/run-all`);
                if (!res.ok) throw new Error('Failed to trigger run');
                toast({
                  title: `Triggered all tests in ${displayName}`,
                  status: 'success',
                  duration: 3000,
                  isClosable: true,
                });
              } catch (err) {
                toast({
                  title: `Failed to run all tests in ${displayName}`,
                  description: err.message,
                  status: 'error',
                  duration: 4000,
                  isClosable: true,
                });
              }
            };
            // Handler for View History
            const handleHistory = () => {
              navigate(`/test-groups/${encodeURIComponent(group)}/history`);
            };
            // Display group name and run date
            const cardDisplayName = `${displayName} (${runDate ? new Date(runDate).toLocaleString() : '-'})`;
                          // Ensure unique key by using the full module path
              const uniqueKey = moduleData?.module || group;
              
              return (
                <TestGroupCard
                  key={uniqueKey}
                  group={cardDisplayName}
                  metrics={metrics}
                  tests={[]} // No individual test cases for modules
                  onView={() => openTestGroupDrawer(group)}
                  onRunAll={handleRunAll}
                  onHistory={handleHistory}
                />
              );
          })}
        </SimpleGrid>
      )}
      {/* Pagination */}
      <HStack mt={8} spacing={4} justify="center" flexShrink={0}>
        <Button
          size="sm"
          onClick={() => handlePageChange(currentPage - 1)}
          isDisabled={currentPage <= 1 || totalPages === 0}
          colorScheme="green"
          variant="outline"
        >
          Previous
        </Button>
        <Text fontSize="sm">Page {currentPage} of {Math.max(1, totalPages)}</Text>
        <Button
          size="sm"
          onClick={() => handlePageChange(currentPage + 1)}
          isDisabled={currentPage >= totalPages || totalPages === 0}
          colorScheme="green"
          variant="outline"
        >
          Next
        </Button>
      </HStack>
    </Page>
  );
}

export default TestCaseGroupsPage; 